import { type ReactElement, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { refreshSession, useAuthStore, useInvalidateMe } from '@/entities/user';
import { useConfirmEmailChange } from '@/features/auth';
import { MY_PROFILE_PATH } from '@/shared/config';
import { getErrorMessage } from '@/shared/lib';
import { buttonVariants, cn } from '@/shared/ui';
import { AuthShell } from '@/widgets/auth-shell';

type ConfirmStatus =
  | { readonly step: 'pending' }
  /** signedIn — 이 브라우저에 로그인돼 있고 새 이메일의 세션을 받았는지. 버튼의 목적지를 가른다. */
  | { readonly step: 'success'; readonly email: string; readonly signedIn: boolean }
  | { readonly step: 'error'; readonly message: string };

const CONTINUE_BUTTON_CLASS =
  'mt-6 h-14 w-full rounded-none bg-primary text-[15px] font-semibold text-white hover:bg-primary/90 dark:text-primary-foreground';

/**
 * 메일 링크(/confirm-email-change?token=...) 도착 페이지. 비로그인 상태에서도 열린다 —
 * 다른 기기 · 브라우저에서 링크를 열 수 있기 때문이다.
 */
export const ConfirmEmailChangePage = (): ReactElement => {
  const [params] = useSearchParams();
  const token = params.get('token');
  const { mutateAsync } = useConfirmEmailChange();
  const invalidateMe = useInvalidateMe();
  const [status, setStatus] = useState<ConfirmStatus>({ step: 'pending' });
  // StrictMode 의 이중 마운트에서 같은 토큰이 두 번 소비되지 않게 토큰별로 1회만 실행한다.
  const firedToken = useRef<string | null>(null);

  useEffect(() => {
    if (!token || firedToken.current === token) {
      return;
    }

    firedToken.current = token;

    // 화면 전환은 훅 상태가 아니라 promise 로 구동한다 — StrictMode 재마운트가
    // 진행 중 뮤테이션에서 옵저버를 분리해 훅 상태가 갱신되지 않는다.
    mutateAsync({ token })
      .then(async ({ email, userId }) => {
        // 로그인 상태면 액세스 토큰·스토어의 이메일이 옛값이므로 세션을 재발급받고 갱신한다.
        // 재발급이 실패하면 세션을 비워 재로그인으로 유도한다. (변경 자체는 이미 성공)
        const wasSignedIn = useAuthStore.getState().isAuthenticated;
        const signedIn = wasSignedIn && (await refreshSession());
        if (wasSignedIn && !signedIn) {
          useAuthStore.getState().clear();
        }
        if (signedIn) {
          const { user, setUser } = useAuthStore.getState();
          // 다른 계정으로 로그인된 브라우저에서 링크를 연 경우 그 계정의 스토어를 덮어쓰지 않는다.
          if (user !== null && user.id === userId) {
            setUser({ ...user, email });
          }
          await invalidateMe();
        }
        setStatus({ step: 'success', email, signedIn });
      })
      .catch((error: unknown) => {
        setStatus({
          step: 'error',
          message: getErrorMessage(error, '이메일 변경에 실패했습니다.'),
        });
      });
  }, [token, mutateAsync, invalidateMe]);

  if (!token) {
    return (
      <AuthShell title="이메일 변경" description="확인 링크가 올바르지 않습니다.">
        <p className="text-sm text-muted-foreground">
          메일의 링크를 그대로 열었는지 확인해 주세요.
        </p>
      </AuthShell>
    );
  }

  if (status.step === 'error') {
    return (
      <AuthShell title="이메일 변경 실패" description="링크가 만료되었거나 유효하지 않습니다.">
        <p
          role="alert"
          className="border-l-2 border-destructive bg-destructive/5 px-4 py-3 text-sm text-destructive"
        >
          {status.message}
        </p>
        <p className="mt-4 text-sm text-muted-foreground">
          회원정보 화면에서 이메일 변경을 다시 요청할 수 있습니다.
        </p>
        <Link to="/sign-in" className={cn(buttonVariants(), CONTINUE_BUTTON_CLASS)}>
          로그인으로 이동
        </Link>
      </AuthShell>
    );
  }

  if (status.step === 'success') {
    return (
      <AuthShell title="이메일 변경 완료" description="이메일이 변경되었습니다.">
        <p className="border-l-2 border-brand-lime bg-muted/70 px-4 py-3 text-[15px] leading-relaxed">
          이메일이 {status.email} 로 변경되었습니다. 앞으로 로그인과 알림에 이 주소를 사용합니다.
        </p>
        {status.signedIn ? (
          <Link to={MY_PROFILE_PATH} className={cn(buttonVariants(), CONTINUE_BUTTON_CLASS)}>
            회원정보로
          </Link>
        ) : (
          <p className="mt-4 text-sm text-muted-foreground">
            <Link
              to="/sign-in"
              className="font-semibold text-foreground underline underline-offset-4 hover:opacity-70"
            >
              로그인
            </Link>{' '}
            후 회원정보에서 변경된 이메일을 확인하실 수 있습니다.
          </p>
        )}
      </AuthShell>
    );
  }

  return (
    <AuthShell title="이메일 변경" description="변경을 처리하고 있습니다…">
      <p className="text-sm text-muted-foreground">잠시만 기다려 주세요.</p>
    </AuthShell>
  );
};
